import { z } from 'zod';

/**
 * F13 account-limits types + Zod schemas.
 *
 * Decisions: see plans/13_F13_staff_permissions_and_trainer_affiliation.md
 * (Decision 12).
 *
 * ─── Reservation seam for F38 subscription tiers ─────────────────────────────
 * F13 ships the table + service-layer enforcement; F38 ships the actual tier
 * values via `AccountLimitsService.set(b2bAccountId, limits, actorAdminId)`.
 * At MVP every row is `(maxStaffSeats=NULL, maxActiveTrainerAffiliations=NULL)`
 * — NULL means "unlimited".
 *
 * One row per `b2b_accounts` row, auto-created in the F11
 * `B2bAccountService.create()` flow extended in F13. The migration backfills
 * the same shape for every pre-existing account so reads never return null.
 *
 * ─── Enforcement semantics ───────────────────────────────────────────────────
 * Enforcement is service-layer only (DB CHECK constraints only guarantee the
 * cap value is non-negative). Limits are NOT enforced retroactively:
 *   - F38 sets `maxStaffSeats=5` on an account with 7 existing members
 *   - all 7 keep working (no auto-removal)
 *   - the 8th invite or accept is refused with 422 SEAT_LIMIT_EXCEEDED
 *
 * Suspended members still occupy a seat (per Decision 12). Only `removed` and
 * fully-terminal affiliation rows free the slot.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * Discriminator passed back in the 422 SEAT_LIMIT_EXCEEDED error body so the
 * portal can route the user to the right help copy ("Upgrade plan to add more
 * staff" vs. "...more trainer affiliations").
 */
// F36 — added 'club_team' + 'club_trainer' (NULL=unlimited at MVP; F38 sets
// per-tier caps). The enforce helpers exist + are wired by F36 but always
// early-return until F38 populates the columns.
export const SEAT_LIMIT_RESOURCES = ['staff', 'affiliation', 'club_team', 'club_trainer'] as const;
export type SeatLimitResource = (typeof SEAT_LIMIT_RESOURCES)[number];

/**
 * MVP defaults applied to every row created via the F13 migration backfill +
 * the F11 account-create extension. F38 (subscription tiers) replaces these
 * with real per-tier values via `AccountLimitsService.set()`.
 *
 * `as const` so the literal `null` types survive into consumers; F38 will
 * grow the constant to `{ MVP, FREE, GROWTH, ENTERPRISE }` etc. without
 * disturbing the existing key.
 */
export const SEAT_LIMIT_DEFAULTS = {
  MVP: { maxStaffSeats: null, maxActiveTrainerAffiliations: null },
} as const;

// ─── Raw DB row shape (mirrors packages/db/src/schema.ts accountLimits) ──────

export type AccountLimits = {
  readonly b2bAccountId: string;
  readonly maxStaffSeats: number | null;
  readonly maxActiveTrainerAffiliations: number | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const SeatLimitResourceSchema = z.enum(SEAT_LIMIT_RESOURCES);

/**
 * F38 calls this when a subscription tier change kicks in. Both fields
 * accept null (= "unlimited"). Negative values rejected at the schema level
 * (mirrors the DB CHECK constraints `account_limits_max_*_nonneg`).
 *
 * Both fields are optional so F38 can patch one at a time.
 */
export const UpdateAccountLimitsInputSchema = z
  .object({
    maxStaffSeats: z.number().int().min(0).nullable().optional(),
    maxActiveTrainerAffiliations: z.number().int().min(0).nullable().optional(),
  })
  .strict();
export type UpdateAccountLimitsInput = z.infer<typeof UpdateAccountLimitsInputSchema>;
